// === IMPORTS ===
import React, { useState, useEffect, useRef } from 'react';
import {
  OfficeLayout,
  OfficeRoom,
  RoomType,
  RoomInteractionPolicy,
} from '../types';
import { DEFAULT_ROOM_TEMPLATES, RoomTemplate } from '../data/officeCatalog';
import {
  X,
  Layers,
  Plus,
  Sparkles,
  Users,
  Zap,
  ChevronDown,
  ChevronUp,
  MapPin,
  Fingerprint,
  Ruler,
  Minus, 
} from 'lucide-react';
import AppIcon from './AppIcon';
import { createNewOfficeRoom } from '../utils/roomUtils';
import { findFreeRoomPosition } from '../utils/isoMath';
import '../styles/twin-drawers-shared.css';
import '../styles/twin-icons-shared.css';
import './CreateZoneDrawer.css';

export const ROOM_TYPE_OPTIONS: { id: RoomType; label: string; icon: string; defaultColor: string }[] = [
  { id: 'work', label: 'Development / Work', icon: 'laptop', defaultColor: '#3b82f6' },
  { id: 'meeting', label: 'Meeting Room', icon: 'users', defaultColor: '#8b5cf6' },
  { id: 'human_interaction', label: 'Human Interaction', icon: 'user-check', defaultColor: '#f97316' },
  { id: 'breakroom', label: 'Cafeteria / Breakroom', icon: 'coffee', defaultColor: '#f59e0b' },
  { id: 'restroom', label: 'Restrooms', icon: 'bath', defaultColor: '#06b6d4' },
  { id: 'server_room', label: 'Servers & DevOps', icon: 'server', defaultColor: '#10b981' },
  { id: 'lounge', label: 'Relax / Quiet Lounge', icon: 'sofa', defaultColor: '#ec4899' },
  { id: 'reception', label: 'Reception / Hall', icon: 'building', defaultColor: '#6366f1' },
  { id: 'custom', label: 'Custom Room', icon: 'sparkles', defaultColor: '#14b8a6' },
];

const PRESET_COLOR_SWATCHES = [
  '#3b82f6', '#8b5cf6', '#f97316', '#f59e0b', '#10b981',
  '#06b6d4', '#ec4899', '#6366f1', '#14b8a6', '#ef4444',
];

interface CreateZoneDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  layout: OfficeLayout;
  onLayoutChange: (layout: OfficeLayout) => void;
  onSelectRoom: (roomId: string | null) => void;
  onOverlapPrevented?: (targetRoomName: string, conflictingRoomName: string) => void;
  initialTemplate?: RoomTemplate | null;
  initialCol?: number;
  initialRow?: number;
  setRoomDraftBounds?: (
    bounds: {
      col: number;
      row: number;
      width: number;
      height: number;
      color?: string;
      name?: string;
      icon?: string;
    } | null
  ) => void;
}

// === COMPONENTE: DRAWER DE CREACIÓN DE ZONAS ===
export const CreateZoneDrawer: React.FC<CreateZoneDrawerProps> = ({
  isOpen,
  onClose,
  layout,
  onLayoutChange,
  onSelectRoom,
  onOverlapPrevented,
  initialTemplate,
  initialCol,
  initialRow,
  setRoomDraftBounds,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const tileSize = layout.tileSizeMeters ?? 1.0;
  const rooms = layout.rooms || [];

  // === ESTADO DEL FORMULARIO ===
  const [name, setName] = useState<string>('New Work Room');
  const [type, setType] = useState<RoomType>('work');
  const [icon, setIcon] = useState<string>('laptop');
  const [color, setColor] = useState<string>('#3b82f6');
  const [width, setWidth] = useState<number>(4);
  const [height, setHeight] = useState<number>(4);
  const [col, setCol] = useState<number>(1);
  const [row, setRow] = useState<number>(1);
  const [energyDelta, setEnergyDelta] = useState<number>(0.2);
  const [focusBonus, setFocusBonus] = useState<number>(10);
  const [bladderDelta, setBladderDelta] = useState<number>(0.5);
  const [maxCapacity, setMaxCapacity] = useState<number>(4);
  const [ambientStatusText, setAmbientStatusText] = useState<string>('Focused at workstation');
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('');

  // === APLICAR PLANTILLA ===
  const applyTemplate = (template: RoomTemplate) => {
    setSelectedTemplateName(template.name);
    setName(template.name);
    setType(template.type);
    setIcon(template.icon);
    setColor(template.color);
    setWidth(template.defaultWidth);
    setHeight(template.defaultHeight);
    if (template.defaultPolicy) {
      setEnergyDelta(template.defaultPolicy.energyDelta ?? 0.2);
      setFocusBonus(template.defaultPolicy.focusBonus ?? 10);
      setBladderDelta(template.defaultPolicy.bladderDelta ?? 0.5);
      setMaxCapacity(template.defaultPolicy.maxCapacity ?? 4);
      setAmbientStatusText(template.defaultPolicy.ambientStatusText || 'Inside the zone');
    }
    const freePos = findFreeRoomPosition(
      template.defaultWidth,
      template.defaultHeight,
      layout.gridWidth,
      layout.gridHeight,
      rooms
    );
    if (freePos) {
      setCol(freePos.col);
      setRow(freePos.row);
    }
  };

  // === RESET / INICIALIZACIÓN AL ABRIR ===
  useEffect(() => {
    if (isOpen) {
      if (initialTemplate) {
        applyTemplate(initialTemplate);
      } else {
        setSelectedTemplateName('');
        setName('New Work Room');
        setType('work');
        setIcon('laptop');
        setColor('#3b82f6');
        setWidth(4);
        setHeight(4);
        setEnergyDelta(0.2);
        setFocusBonus(10);
        setBladderDelta(0.5);
        setMaxCapacity(4);
        setAmbientStatusText('Focused at workstation');

        const targetCol = initialCol !== undefined ? initialCol : 1;
        const targetRow = initialRow !== undefined ? initialRow : 1;

        if (initialCol === undefined || initialRow === undefined) {
          const freePos = findFreeRoomPosition(4, 4, layout.gridWidth, layout.gridHeight, rooms);
          if (freePos) {
            setCol(freePos.col);
            setRow(freePos.row);
          } else {
            setCol(1);
            setRow(1);
          }
        } else {
          setCol(targetCol);
          setRow(targetRow);
        }
      }
    }
  }, [isOpen, initialTemplate, initialCol, initialRow]);

  // === SINCRONIZAR VISTA PREVIA EN EL CANVAS ===
  useEffect(() => {
    if (isOpen && setRoomDraftBounds) {
      setRoomDraftBounds({ col, row, width, height, color, name, icon });
    } else if (!isOpen && setRoomDraftBounds) {
      setRoomDraftBounds(null);
    }
    return () => {
      if (setRoomDraftBounds) {
        setRoomDraftBounds(null);
      }
    };
  }, [isOpen, col, row, width, height, color, name, icon, setRoomDraftBounds]);

  // === CIERRE POR CLIC AFUERA O TECLA ESCAPE ===
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (drawerRef.current && drawerRef.current.contains(target)) return;
      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) return;
      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // === CONSTRUIR LA ZONA ===
  const handleBuildRoom = () => {
    if (!name.trim()) return;

    const result = createNewOfficeRoom({
      name: name.trim(),
      type,
      color,
      icon,
      col,
      row,
      width,
      height,
      policy: {
        energyDelta,
        focusBonus,
        bladderDelta,
        maxCapacity,
        ambientStatusText: ambientStatusText.trim() || undefined,
      },
      gridWidth: layout.gridWidth,
      gridHeight: layout.gridHeight,
      existingRooms: rooms,
      autoRelocateOnOverlap: false,
    });

    if (!result.success && result.overlapConflict) {
      if (onOverlapPrevented) {
        onOverlapPrevented(result.room.name, result.overlapConflict.name);
      }
      return;
    }

    const updatedRooms = [...rooms, result.room];
    onLayoutChange({ ...layout, rooms: updatedRooms });

    if (setRoomDraftBounds) setRoomDraftBounds(null);
    onSelectRoom(result.room.id);
    onClose();
  };

  const calculateAreaM2 = (w: number, h: number) => (w * tileSize * (h * tileSize)).toFixed(1);

  // === RENDER ===
  return (
    <aside
      ref={drawerRef}
      id="create-zone-drawer"
      aria-label="Side Drawer: Build New Zone"
      className={`twin-drawer twin-drawer--lg ${isOpen ? 'twin-drawer--open' : 'twin-drawer--closed'}`}
    >
      {/* === HEADER === */}
      <div className="twin-drawer-header">
        <div className="twin-drawer-header-left">
          <div className="twin-drawer-icon-badge twin-zone-icon-badge">
            <Layers className="twin-icon-sm" />
          </div>
          <div className="twin-drawer-header-text">
          <span className="twin-drawer-title">Build New Zone</span>
          <span className="twin-drawer-subtitle twin-zone-grid-badge">
            Grid: {layout.gridWidth}×{layout.gridHeight} tiles
          </span>
        </div>
        </div>
        <button type="button" onClick={onClose} className="twin-drawer-close-btn" title="Close drawer (Esc)">
          <X className="twin-icon-sm" />
        </button>
      </div>

      {/* === CUERPO === */}
      <div className="twin-drawer-body">
        {/* === PLANTILLAS RÁPIDAS === */}
        <div className="twin-drawer-card">
          <div className="twin-row-between">
            <label className="twin-field-label twin-inline-icon-label twin-zone-accent-text twin-no-margin">
              <Sparkles className="twin-icon-xs twin-zone-accent-icon" />
              <h3 className="twin-section-heading">Quick Templates</h3>
            </label>
            <span className="twin-zone-hint-tag">1-click</span>
          </div>
          <div className="twin-template-grid">
            {DEFAULT_ROOM_TEMPLATES.map((tmpl) => {
              const isSelected = selectedTemplateName === tmpl.name;
              return (
                <button
                  key={tmpl.name}
                  type="button"
                  onClick={() => applyTemplate(tmpl)}
                  className={`twin-template-btn ${isSelected ? 'twin-template-btn--selected' : ''}`}
                >
                  <AppIcon icon={tmpl.icon} type={tmpl.type} className="twin-icon-sm twin-zone-accent-icon" />
                  <div className="twin-template-btn-text">
                    <div className="twin-template-btn-name">{tmpl.name}</div>
                    <div className="twin-template-btn-size">{tmpl.defaultWidth}x{tmpl.defaultHeight}m</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* === IDENTIFICACIÓN Y TIPO === */}
        <div className="twin-drawer-card">
           <div className="twin-section-header">
            <Fingerprint className="twin-icon-xs" />
            <h3 className="twin-section-heading">1. Identification & Purpose</h3>
          </div>

          <div>
            <label className="twin-field-label">Zone Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setSelectedTemplateName(''); }}
              placeholder="e.g. Server Room, Open Space..."
              className="twin-input twin-input--zone"
            />
          </div>

          <div>
            <label className="twin-field-label">Type / Purpose</label>
            <div className="twin-select-wrap">
              <select
                value={type}
                onChange={(e) => {
                  const nextType = e.target.value as RoomType;
                  setType(nextType);
                  const matched = ROOM_TYPE_OPTIONS.find((opt) => opt.id === nextType);
                  if (matched) {
                    setIcon(matched.icon);
                    setColor(matched.defaultColor);
                  }
                  setSelectedTemplateName('');
                }}
                className="twin-select twin-input--zone twin-select--with-chevron"
              >
                {ROOM_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="twin-icon-xs twin-select-chevron" />
            </div>
          </div>

          <div>
            <label className="twin-field-label twin-row-between">
              <span>Theme Color</span>
              <span className="twin-color-hex">{color}</span>
            </label>
            <div className="twin-swatch-row">
              {PRESET_COLOR_SWATCHES.map((swatch) => (
                <button
                  key={swatch}
                  type="button"
                  onClick={() => setColor(swatch)}
                  className={`twin-color-swatch ${color === swatch ? 'twin-color-swatch--selected' : ''}`}
                  style={{ backgroundColor: swatch }}
                />
              ))}
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="twin-color-native"
                title="Custom color"
              />
            </div>
          </div>
        </div>

        {/* === DIMENSIONES ESPACIALES === */}
        <div className="twin-drawer-card">
          <div className="twin-section-header twin-row-between">
            <div className="twin-row-between" style={{ gap: '0.5rem' }}>
              <Ruler className="twin-icon-xs" />
              <h3 className="twin-section-heading twin-no-margin">2. Spatial Dimensions</h3>
            </div>
            <span className="twin-area-badge">
              {calculateAreaM2(width, height)} m² ({width}×{height} tiles)
            </span>
          </div>

          <div className="twin-grid-2 twin-drawer-card--tight">
            <div>
              <label className="twin-field-label">Width (Tiles):</label>
              <div className="twin-stepper">
                <button type="button" onClick={() => setWidth((prev) => Math.max(2, prev - 1))} className="twin-stepper-btn">
                  <Minus className="twin-icon-xs" />
                </button>
                <input
                  type="number"
                  min={2}
                  max={layout.gridWidth}
                  value={width}
                  onChange={(e) => setWidth(Math.max(2, Math.min(layout.gridWidth, parseInt(e.target.value, 10) || 2)))}
                  className="twin-stepper-input"
                />
                <button type="button" onClick={() => setWidth((prev) => Math.min(layout.gridWidth, prev + 1))} className="twin-stepper-btn">
                  <ChevronUp className="twin-icon-xs" />
                </button>
              </div>
            </div>

            <div>
              <label className="twin-field-label">Height (Tiles):</label>
              <div className="twin-stepper">
                <button type="button" onClick={() => setHeight((prev) => Math.max(2, prev - 1))} className="twin-stepper-btn">
                  <Minus className="twin-icon-xs" />
                </button>
                <input
                  type="number"
                  min={2}
                  max={layout.gridHeight}
                  value={height}
                  onChange={(e) => setHeight(Math.max(2, Math.min(layout.gridHeight, parseInt(e.target.value, 10) || 2)))}
                  className="twin-stepper-input"
                />
                <button type="button" onClick={() => setHeight((prev) => Math.min(layout.gridHeight, prev + 1))} className="twin-stepper-btn">
                  <Plus className="twin-icon-xs" />
                </button>             
                </div>
            </div>
          </div>

          <div className="twin-coord-section">
            <div className="twin-row-between">
              <span className="twin-field-label twin-inline-icon-label twin-no-margin">
                {/*<MapPin className="twin-icon-xs twin-zone-accent-icon" />*/}
                <span>Origin Coordinates (Col, Row):</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  const freePos = findFreeRoomPosition(width, height, layout.gridWidth, layout.gridHeight, rooms);
                  if (freePos) { setCol(freePos.col); setRow(freePos.row); }
                }}
                className="twin-find-spot-link"
              >
                Find free spot
              </button>
            </div>

            <div className="twin-grid-2 ">
              <div className="twin-coord-input-group">
                <span className="twin-coord-label">Col:</span>
                <input
                  type="number"
                  min={0}
                  max={Math.max(0, layout.gridWidth - width)}
                  value={col}
                  onChange={(e) => setCol(Math.max(0, Math.min(layout.gridWidth - width, parseInt(e.target.value, 10) || 0)))}
                  className="twin-coord-input"
                />
              </div>
              <div className="twin-coord-input-group">
                <span className="twin-coord-label">Row:</span>
                <input
                  type="number"
                  min={0}
                  max={Math.max(0, layout.gridHeight - height)}
                  value={row}
                  onChange={(e) => setRow(Math.max(0, Math.min(layout.gridHeight - height, parseInt(e.target.value, 10) || 0)))}
                  className="twin-coord-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* === POLÍTICAS DE INTERACCIÓN Y BIENESTAR === */}
        <div className="twin-drawer-card">
          <label className="twin-section-heading">3. Interaction & Well-Being Policies</label>

          <div>
            <label className="twin-field-label">Agent Ambient Status</label>
            <input
              type="text"
              value={ambientStatusText}
              onChange={(e) => setAmbientStatusText(e.target.value)}
              placeholder="e.g. In collaborative working session..."
              className="twin-input twin-input--zone"
            />
          </div>

          <div className="twin-grid-2">
            <div>
              <label className="twin-field-label twin-inline-icon-label">
                {/*<Users className="twin-icon-xs twin-zone-accent-icon" />*/}
                <span>Max Capacity:</span>
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={maxCapacity}
                onChange={(e) => setMaxCapacity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="twin-input twin-input--zone twin-input--bold"
              />
            </div>

            <div>
              <label className="twin-field-label twin-inline-icon-label">
                {/*<Zap className="twin-icon-xs twin-zone-focus-icon" />*/}
                <span>Focus Bonus (%):</span>
              </label>
              <input
                type="number"
                min={0}
                max={100}
                value={focusBonus}
                onChange={(e) => setFocusBonus(Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="twin-input twin-input--zone twin-input--bold"
              />
            </div>
          </div>
        </div>
      </div>

      {/* === FOOTER === */}
      <div className="twin-drawer-footer">
        <button type="button" onClick={onClose} className="twin-btn-cancel">Cancel</button>
        <button
          type="button"
          onClick={handleBuildRoom}
          disabled={!name.trim()}
          className="twin-btn-save"
        >
          <Plus className="twin-icon-sm" />
          <span>Build Zone</span>
        </button>
      </div>
    </aside>
  );
};